'use client';

import { SubmissionResult, useForm } from '@conform-to/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import * as Popover from '@radix-ui/react-popover';
import { clsx } from 'clsx';
import debounce from 'lodash.debounce';
import {
  ArrowRight,
  ChevronDown,
  GiftIcon,
  LogOut,
  Search,
  SearchIcon,
  ShoppingBag,
  User,
} from 'lucide-react';
import { useParams, useSearchParams } from 'next/navigation';
import React, {
  forwardRef,
  Ref,
  useActionState,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { useFormStatus } from 'react-dom';

import { FormStatus } from '@/vibes/soul/form/form-status';
import { Stream, Streamable } from '@/vibes/soul/lib/streamable';
import { Button } from '@/vibes/soul/primitives/button';
import { Logo } from '@/vibes/soul/primitives/logo';
import { Price } from '@/vibes/soul/primitives/price-label';
import { ProductCard } from '@/vibes/soul/primitives/product-card';
import { Link } from '~/components/link';
import { usePathname, useRouter } from '~/i18n/routing';
import { useSearch } from '~/lib/search';

interface LinkGroup {
  label?: string;
  href?: string;
  links: Array<{
    label: string;
    href: string;
  }>;
}

/**
 * A top-level item. Its groups and its columns come together or not at all:
 * the mobile menu lists the groups and the desktop dropdown draws the
 * columns, so one without the other would show an item's children on one
 * and not the other.
 */
type Link = {
  label: string;
  href: string;
  image?: { src: string; alt: string };
} & (
  | {
      /** Every group in set order: the mobile menu lists these. */
      groups: LinkGroup[];
      /** The desktop dropdown's columns, left to right; each column's groups top to bottom. */
      columns: LinkGroup[][];
    }
  | { groups?: never; columns?: never }
);

interface Locale {
  id: string;
  label: string;
}

type Action<State, Payload> = (
  state: Awaited<State>,
  payload: Awaited<Payload>,
) => State | Promise<State>;

export type SearchResult =
  | {
      type: 'products';
      title: string;
      products: Array<{
        id: string;
        title: string;
        href: string;
        price?: Price;
        image?: { src: string; alt: string };
      }>;
    }
  | {
      type: 'links';
      title: string;
      links: Array<{ label: string; href: string }>;
    };

type SearchAction<S extends SearchResult> = Action<
  {
    searchResults: S[] | null;
    lastResult: SubmissionResult | null;
    emptyStateTitle?: string;
    emptyStateSubtitle?: string;
  },
  FormData
>;

interface AccountUser {
  firstName: string | null;
  email: string | null;
}

interface Props<S extends SearchResult> {
  className?: string;
  isFloating?: boolean;
  accountHref: string;
  accountUser?: Streamable<AccountUser | null>;
  loginHref?: string;
  cartCount?: Streamable<number | null>;
  cartHref: string;
  links: Streamable<Link[]>;
  linksPosition?: 'center' | 'left' | 'right';
  locales?: Locale[];
  activeLocaleId?: string;
  logo?: Streamable<string | { src: string; alt: string } | null>;
  logoWidth?: number;
  logoHeight?: number;
  logoHref?: string;
  logoLabel?: string;
  mobileLogo?: Streamable<string | { src: string; alt: string } | null>;
  mobileLogoWidth?: number;
  mobileLogoHeight?: number;
  searchHref: string;
  searchParamName?: string;
  searchAction?: SearchAction<S>;
  searchInputPlaceholder?: string;
  searchSubmitLabel?: string;
  cartLabel?: string;
  accountLabel?: string;
  openSearchPopupLabel?: string;
  searchLabel?: string;
  mobileMenuTriggerLabel?: string;
  giftCertificatesLabel?: string;
  giftCertificatesHref: string;
  giftCertificatesEnabled?: Streamable<boolean>;
}

const MobileMenuButton = forwardRef<
  React.ComponentRef<'button'>,
  { open: boolean } & React.ComponentPropsWithoutRef<'button'>
>(({ open, className, ...rest }, ref) => {
  return (
    <button
      {...rest}
      className={clsx(
        'group relative rounded-lg p-2 outline-0 ring-(--nav-focus,hsl(var(--primary))) transition-colors focus-visible:ring-2',
        className,
      )}
      ref={ref}
    >
      <div className="flex h-4 w-4 origin-center transform flex-col justify-between overflow-hidden transition-all duration-300">
        <div
          className={clsx(
            'h-px origin-left transform bg-(--nav-mobile-button-icon,hsl(var(--foreground))) transition-all duration-300',
            open ? 'translate-x-10' : 'w-7',
          )}
        />
        <div
          className={clsx(
            'h-px transform rounded bg-(--nav-mobile-button-icon,hsl(var(--foreground))) transition-all delay-75 duration-300',
            open ? 'translate-x-10' : 'w-7',
          )}
        />
        <div
          className={clsx(
            'h-px origin-left transform bg-(--nav-mobile-button-icon,hsl(var(--foreground))) transition-all delay-150 duration-300',
            open ? 'translate-x-10' : 'w-7',
          )}
        />

        <div
          className={clsx(
            'absolute top-2 flex transform items-center justify-between bg-(--nav-mobile-button-icon,hsl(var(--foreground))) transition-all duration-500',
            open ? 'w-12 translate-x-0' : 'w-0 -translate-x-10',
          )}
        >
          <div
            className={clsx(
              'absolute h-px w-4 transform bg-(--nav-mobile-button-icon,hsl(var(--foreground))) transition-all delay-300 duration-500',
              open ? 'rotate-45' : 'rotate-0',
            )}
          />
          <div
            className={clsx(
              'absolute h-px w-4 transform bg-(--nav-mobile-button-icon,hsl(var(--foreground))) transition-all delay-300 duration-500',
              open ? '-rotate-45' : 'rotate-0',
            )}
          />
        </div>
      </div>
    </button>
  );
});

MobileMenuButton.displayName = 'MobileMenuButton';

const navGroupClassName =
  'block rounded-lg bg-(--nav-group-background,transparent) px-3 py-2 font-[family-name:var(--nav-group-font-family,var(--font-family-body))] font-medium text-(--nav-group-text,hsl(var(--foreground))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-group-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-group-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2';
const navButtonClassName =
  'relative rounded-lg bg-(--nav-button-background,transparent) p-1.5 text-(--nav-button-icon,hsl(var(--foreground))) ring-(--nav-focus,hsl(var(--primary))) transition-colors focus-visible:outline-0 focus-visible:ring-2 @4xl:hover:bg-(--nav-button-background-hover,hsl(var(--contrast-100))) @4xl:hover:text-(--nav-button-icon-hover,hsl(var(--foreground)))';

/**
 * This component supports various CSS variables for theming. Here's a comprehensive list, along
 * with their default values:
 *
 * ```css
 * :root {
 *   --nav-focus: hsl(var(--primary));
 *   --nav-background: hsl(var(--background));
 *   --nav-floating-border: hsl(var(--foreground) / 10%);
 *   --nav-link-text: hsl(var(--foreground));
 *   --nav-link-text-hover: hsl(var(--foreground));
 *   --nav-link-background: transparent;
 *   --nav-link-background-hover: hsl(var(--contrast-100));
 *   --nav-link-font-family: var(--font-family-body);
 *   --nav-group-text: hsl(var(--foreground));
 *   --nav-group-text-hover: hsl(var(--foreground));
 *   --nav-group-background: transparent;
 *   --nav-group-background-hover: hsl(var(--contrast-100));
 *   --nav-group-font-family: var(--font-family-body);
 *   --nav-sub-link-text: hsl(var(--contrast-500));
 *   --nav-sub-link-text-hover: hsl(var(--foreground));
 *   --nav-sub-link-background: transparent;
 *   --nav-sub-link-background-hover: hsl(var(--contrast-100));
 *   --nav-sub-link-font-family: var(--font-family-body);
 *   --nav-button-icon: hsl(var(--foreground));
 *   --nav-button-icon-hover: hsl(var(--foreground));
 *   --nav-button-background: hsl(var(--background));
 *   --nav-button-background-hover: hsl(var(--contrast-100));
 *   --nav-menu-background: hsl(var(--background));
 *   --nav-menu-border: hsl(var(--foreground) / 5%);
 *   --nav-mobile-background: hsl(var(--background));
 *   --nav-mobile-divider: hsl(var(--contrast-100));
 *   --nav-mobile-button-icon: hsl(var(--foreground));
 *   --nav-mobile-link-text: hsl(var(--foreground));
 *   --nav-mobile-link-text-hover: hsl(var(--foreground));
 *   --nav-mobile-link-background: transparent;
 *   --nav-mobile-link-background-hover: hsl(var(--contrast-100));
 *   --nav-mobile-link-font-family: var(--font-family-body);
 *   --nav-mobile-sub-link-text: hsl(var(--contrast-500));
 *   --nav-mobile-sub-link-text-hover: hsl(var(--foreground));
 *   --nav-mobile-sub-link-background: transparent;
 *   --nav-mobile-sub-link-background-hover: hsl(var(--contrast-100));
 *   --nav-mobile-sub-link-font-family: var(--font-family-body);
 *   --nav-search-background: hsl(var(--background));
 *   --nav-search-border: hsl(var(--foreground) / 5%);
 *   --nav-search-divider: hsl(var(--foreground) / 5%);
 *   --nav-search-icon: hsl(var(--contrast-500));
 *   --nav-search-empty-title: hsl(var(--foreground));
 *   --nav-search-empty-subtitle: hsl(var(--contrast-500));
 *   --nav-search-result-title: hsl(var(--foreground));
 *   --nav-search-result-title-font-family: var(--font-family-mono);
 *   --nav-search-result-link-text: hsl(var(--foreground));
 *   --nav-search-result-link-text-hover: hsl(var(--foreground));
 *   --nav-search-result-link-background: hsl(var(--background));
 *   --nav-search-result-link-background-hover: hsl(var(--contrast-100));
 *   --nav-search-result-link-font-family: var(--font-family-body);
 *   --nav-cart-count-text: hsl(var(--background));
 *   --nav-cart-count-background: hsl(var(--foreground));
 *   --nav-locale-background: hsl(var(--background));
 *   --nav-locale-link-text: hsl(var(--contrast-400));
 *   --nav-locale-link-text-hover: hsl(var(--foreground));
 *   --nav-locale-link-text-selected: hsl(var(--foreground));
 *   --nav-locale-link-background: transparent;
 *   --nav-locale-link-background-hover: hsl(var(--contrast-100));
 *   --nav-locale-link-font-family: var(--font-family-body);
 * }
 * ```
 */
export const Navigation = forwardRef(function Navigation<S extends SearchResult>(
  {
    className,
    isFloating = false,
    cartHref,
    cartCount: streamableCartCount,
    accountHref,
    accountUser: streamableAccountUser,
    loginHref = '/login',
    links: streamableLinks,
    logo: streamableLogo,
    logoHref = '/',
    logoLabel = 'Home',
    logoWidth = 200,
    logoHeight = 40,
    mobileLogo: streamableMobileLogo,
    mobileLogoWidth = 100,
    mobileLogoHeight = 40,
    linksPosition = 'center',
    activeLocaleId,
    locales,
    searchHref,
    searchParamName = 'query',
    searchAction,
    searchInputPlaceholder,
    searchSubmitLabel,
    cartLabel = 'Cart',
    accountLabel = 'Profile',
    openSearchPopupLabel = 'Open search popup',
    searchLabel = 'Search',
    mobileMenuTriggerLabel = 'Toggle navigation',
    giftCertificatesLabel = 'Gift Certificates',
    giftCertificatesHref,
    giftCertificatesEnabled: streamableGiftCertificatesEnabled,
  }: Props<S>,
  ref: Ref<HTMLDivElement>,
) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileCategory, setExpandedMobileCategory] = useState<number | null>(null);
  const [expandedMobileGroup, setExpandedMobileGroup] = useState<string | null>(null);
  const { isSearchOpen, setIsSearchOpen } = useSearch();
  const [navValue, setNavValue] = useState<string>('');
  const router = useRouter();

  const pathname = usePathname();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setExpandedMobileCategory(null);
    setExpandedMobileGroup(null);
    setIsSearchOpen(false);
  }, [pathname, setIsSearchOpen]);

  useEffect(() => {
    function handleScroll() {
      setIsSearchOpen(false);
      setIsMobileMenuOpen(false);
      setNavValue('');
    }

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [setIsSearchOpen]);

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 896px)');
    const handleChange = () => {
      if (mql.matches) {
        setIsMobileMenuOpen(false);
      }
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  return (
    <NavigationMenu.Root
      className={clsx('relative mx-auto w-full max-w-(--breakpoint-2xl) @container', className)}
      delayDuration={0}
      value={navValue}
      onValueChange={(value) => {
        setNavValue(value);
        setIsSearchOpen(false);
      }}
      onMouseLeave={() => setNavValue('')}
      ref={ref}
    >
      <div
        className={clsx(
          'flex items-center justify-between gap-1 bg-(--nav-background,hsl(var(--background))) py-2 pl-3 pr-2 transition-shadow @4xl:rounded-2xl @4xl:px-2 @4xl:pl-6 @4xl:pr-2.5',
          isFloating
            ? 'shadow-xl ring-1 ring-(--nav-floating-border,hsl(var(--foreground)/10%))'
            : 'shadow-none ring-0',
        )}
      >
        {/* Mobile Menu */}
        <Popover.Root
          onOpenChange={(open) => {
            setIsMobileMenuOpen(open);
            if (!open) {
              setExpandedMobileCategory(null);
              setExpandedMobileGroup(null);
            }
          }}
          open={isMobileMenuOpen}
        >
          <Popover.Trigger asChild>
            <MobileMenuButton
              aria-label={mobileMenuTriggerLabel}
              className="mr-1 @4xl:hidden"
              open={isMobileMenuOpen}
            />
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align="start"
              className="max-h-[calc(var(--radix-popover-content-available-height)-8px)] w-(--radix-popper-available-width) @container data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
              sideOffset={8}
            >
              <div className="max-h-[inherit] divide-y divide-(--nav-mobile-divider,hsl(var(--contrast-100))) overflow-y-auto bg-white">
                <Stream
                  fallback={
                    <ul className="flex animate-pulse flex-col gap-4 p-5 @4xl:gap-2 @4xl:p-5">
                      <li>
                        <span className="block h-4 w-10 rounded-md bg-contrast-100" />
                      </li>
                      <li>
                        <span className="block h-4 w-14 rounded-md bg-contrast-100" />
                      </li>
                      <li>
                        <span className="block h-4 w-24 rounded-md bg-contrast-100" />
                      </li>
                      <li>
                        <span className="block h-4 w-16 rounded-md bg-contrast-100" />
                      </li>
                    </ul>
                  }
                  value={streamableLinks}
                >
                  {(links) =>
                    links.map((item, i) => {
                      const allGroups = item.groups ?? [];
                      const hasSubcategories = allGroups.length > 0;
                      const isExpanded = expandedMobileCategory === i;

                      return (
                        <ul className="flex flex-col p-2 @4xl:gap-2 @4xl:p-5" key={i}>
                          {item.label !== '' && (
                            <li>
                              {hasSubcategories ? (
                                <button
                                  className="flex w-full items-center justify-between rounded-lg bg-(--nav-mobile-link-background,transparent) px-3 py-2 font-[family-name:var(--nav-group-font-family,var(--font-family-body))] font-medium text-(--nav-mobile-link-text,hsl(var(--foreground))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-mobile-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-mobile-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2 @4xl:py-4"
                                  onClick={() => {
                                    setExpandedMobileCategory(isExpanded ? null : i);
                                    setExpandedMobileGroup(null);
                                  }}
                                >
                                  {item.label}
                                  <ChevronDown
                                    className={clsx(
                                      'transition-transform duration-200',
                                      isExpanded && 'rotate-180',
                                    )}
                                    size={18}
                                    strokeWidth={1.5}
                                  />
                                </button>
                              ) : (
                                <Link
                                  className="block rounded-lg bg-(--nav-mobile-link-background,transparent) px-3 py-2 font-[family-name:var(--nav-group-font-family,var(--font-family-body))] font-medium text-(--nav-mobile-link-text,hsl(var(--foreground))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-mobile-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-mobile-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2 @4xl:py-4"
                                  href={item.href}
                                >
                                  {item.label}
                                </Link>
                              )}
                            </li>
                          )}
                          {isExpanded && (
                            <>
                              <li>
                                <Link
                                  className="block rounded-lg bg-(--nav-sub-link-background,transparent) py-1.5 pl-6 pr-3 font-[family-name:var(--nav-sub-link-font-family,var(--font-family-body))] text-sm font-normal text-(--nav-sub-link-text,hsl(var(--contrast-500))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-sub-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-sub-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2 @4xl:py-4"
                                  href={item.href}
                                >
                                  Shop All
                                </Link>
                              </li>
                              {allGroups.map((group, gi) => {
                                const groupKey = `${i}-${gi}`;
                                const hasChildren = group.links.length > 0;
                                const isGroupExpanded = expandedMobileGroup === groupKey;

                                if (!group.label) {
                                  return group.links.map((link, li) => (
                                    <li key={`${groupKey}-${li}`}>
                                      <Link
                                        className="block rounded-lg bg-(--nav-sub-link-background,transparent) py-1.5 pl-6 pr-3 font-[family-name:var(--nav-sub-link-font-family,var(--font-family-body))] text-sm font-normal text-(--nav-sub-link-text,hsl(var(--contrast-500))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-sub-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-sub-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2 @4xl:py-4"
                                        href={link.href}
                                      >
                                        {link.label}
                                      </Link>
                                    </li>
                                  ));
                                }

                                return (
                                  <li key={groupKey}>
                                    {hasChildren ? (
                                      <>
                                        <button
                                          className="flex w-full items-center justify-between rounded-lg bg-(--nav-sub-link-background,transparent) py-1.5 pl-6 pr-3 font-[family-name:var(--nav-sub-link-font-family,var(--font-family-body))] text-sm font-medium text-(--nav-sub-link-text,hsl(var(--contrast-500))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-sub-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-sub-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2 @4xl:py-4"
                                          onClick={() =>
                                            setExpandedMobileGroup(
                                              isGroupExpanded ? null : groupKey,
                                            )
                                          }
                                        >
                                          {group.label}
                                          <ChevronDown
                                            className={clsx(
                                              'transition-transform duration-200',
                                              isGroupExpanded && 'rotate-180',
                                            )}
                                            size={14}
                                            strokeWidth={1.5}
                                          />
                                        </button>
                                        {isGroupExpanded && (
                                          <ul className="flex flex-col">
                                            {group.href != null && group.href !== '' && (
                                              <li>
                                                <Link
                                                  className="block rounded-lg bg-(--nav-sub-link-background,transparent) py-1.5 pl-10 pr-3 font-[family-name:var(--nav-sub-link-font-family,var(--font-family-body))] text-sm font-normal text-(--nav-sub-link-text,hsl(var(--contrast-500))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-sub-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-sub-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2"
                                                  href={group.href}
                                                >
                                                  Shop All
                                                </Link>
                                              </li>
                                            )}
                                            {group.links.map((link, li) => (
                                              <li key={li}>
                                                <Link
                                                  className="block rounded-lg bg-(--nav-sub-link-background,transparent) py-1.5 pl-10 pr-3 font-[family-name:var(--nav-sub-link-font-family,var(--font-family-body))] text-sm font-normal text-(--nav-sub-link-text,hsl(var(--contrast-500))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-sub-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-sub-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2"
                                                  href={link.href}
                                                >
                                                  {link.label}
                                                </Link>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </>
                                    ) : (
                                      <Link
                                        className="block rounded-lg bg-(--nav-sub-link-background,transparent) py-1.5 pl-6 pr-3 font-[family-name:var(--nav-sub-link-font-family,var(--font-family-body))] text-sm font-normal text-(--nav-sub-link-text,hsl(var(--contrast-500))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-sub-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-sub-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2 @4xl:py-4"
                                        href={group.href ?? '#'}
                                      >
                                        {group.label}
                                      </Link>
                                    )}
                                  </li>
                                );
                              })}
                            </>
                          )}
                        </ul>
                      );
                    })
                  }
                </Stream>
                {/* Mobile Locale Dropdown */}
                {locales && locales.length > 1 && (
                  <div className="p-2 @4xl:p-5">
                    <div className="flex items-center px-3 py-1 @4xl:py-2">
                      <LocaleSwitcher
                        activeLocaleId={activeLocaleId}
                        locales={locales as [Locale, Locale, ...Locale[]]}
                      />
                    </div>
                  </div>
                )}
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {/* Logo */}
        <div
          className={clsx(
            'flex items-center justify-start self-stretch',
            linksPosition === 'center' ? 'flex-1' : 'flex-1 @4xl:flex-none',
          )}
        >
          <Logo
            className={clsx(streamableMobileLogo != null ? 'hidden @4xl:flex' : 'flex')}
            height={logoHeight}
            href={logoHref}
            label={logoLabel}
            logo={streamableLogo}
            width={logoWidth}
          />
          {streamableMobileLogo != null && (
            <Logo
              className="flex @4xl:hidden"
              height={mobileLogoHeight}
              href={logoHref}
              label={logoLabel}
              logo={streamableMobileLogo}
              width={mobileLogoWidth}
            />
          )}
        </div>

        {/* Top Level Nav Links */}
        <NavigationMenu.List
          className={clsx(
            'hidden gap-1 @4xl:flex @4xl:flex-1',
            {
              left: '@4xl:justify-start',
              center: '@4xl:justify-center',
              right: '@4xl:justify-end',
            }[linksPosition],
          )}
        >
          <Stream
            fallback={
              <ul className="flex min-h-[41px] animate-pulse flex-row items-center @4xl:gap-6 @4xl:p-2.5">
                <li>
                  <span className="block h-4 w-10 rounded-md bg-contrast-100" />
                </li>
                <li>
                  <span className="block h-4 w-14 rounded-md bg-contrast-100" />
                </li>
                <li>
                  <span className="block h-4 w-24 rounded-md bg-contrast-100" />
                </li>
                <li>
                  <span className="block h-4 w-16 rounded-md bg-contrast-100" />
                </li>
              </ul>
            }
            value={streamableLinks}
          >
            {(links) =>
              links.map((item, i) => (
                <NavigationMenu.Item key={i} value={i.toString()}>
                  <NavigationMenu.Trigger
                    className="hidden cursor-pointer items-center whitespace-nowrap rounded-xl border-0 bg-(--nav-link-background,transparent) p-2.5 font-[family-name:var(--nav-link-font-family,var(--font-family-body))] text-sm font-medium text-(--nav-link-text,hsl(var(--foreground))) ring-(--nav-focus,hsl(var(--primary))) transition-colors duration-200 hover:bg-(--nav-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2 @4xl:inline-flex"
                    onClick={() => {
                      if (!pathname.endsWith(item.href) && !pathname.endsWith(item.href + '/')) {
                        router.push(item.href);
                      }
                    }}
                  >
                    {item.label}
                  </NavigationMenu.Trigger>
                  {item.columns != null && item.columns.length > 0 && (
                    <NavigationMenu.Content className="rounded-2xl bg-(--nav-menu-background,hsl(var(--background))) shadow-xl ring-1 ring-(--nav-menu-border,hsl(var(--foreground)/5%))">
                      <div className="m-auto flex w-full max-w-(--breakpoint-lg) gap-8 px-10 pb-8 pt-5">
                        {/* The columns, in the order the shop tree lays them out. Each
                            starts at its narrowest (w-min) and grows to its natural
                            width (max-w-max), so they sit side by side, labels
                            wrapping, until even their narrowest cannot fit; only then
                            does a column wrap to a second row. */}
                        <div className="flex min-w-0 flex-1 flex-wrap gap-x-8 gap-y-6">
                          {item.columns.map((column, columnIdx) => (
                          <div className="flex w-min max-w-max grow flex-col gap-1" key={columnIdx}>
                            {column.map((group, idx) => (
                              <ul className="flex flex-col" key={idx}>
                                {group.label != null && group.label !== '' && (
                                  <li>
                                    {group.href != null && group.href !== '' ? (
                                      <Link className={navGroupClassName} href={group.href}>
                                        {group.label}
                                      </Link>
                                    ) : (
                                      <span className={navGroupClassName}>{group.label}</span>
                                    )}
                                  </li>
                                )}
                                {group.links.map((link, linkIdx) => (
                                  <li key={linkIdx}>
                                    <Link
                                      className="block rounded-lg bg-(--nav-sub-link-background,transparent) px-3 py-1.5 font-[family-name:var(--nav-sub-link-font-family,var(--font-family-body))] text-sm font-normal text-(--nav-sub-link-text,hsl(var(--contrast-500))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-sub-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-sub-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2"
                                      href={link.href}
                                    >
                                      {link.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            ))}
                          </div>
                          ))}
                        </div>

                        {/* Image — right side, large screens only */}
                        {item.image != null && (
                          <div className="hidden w-[186px] shrink-0 self-center @2xl:block">
                            <Link href={item.href}>
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                alt={item.image.alt}
                                className="aspect-3/4 w-full object-cover"
                                src={item.image.src}
                              />
                            </Link>
                          </div>
                        )}
                      </div>
                    </NavigationMenu.Content>
                  )}
                </NavigationMenu.Item>
              ))
            }
          </Stream>
        </NavigationMenu.List>

        {/* Icon Buttons */}
        <div
          className={clsx(
            'flex items-center justify-end gap-0.5 transition-colors duration-300',
            linksPosition === 'center' ? 'flex-1' : 'flex-1 @4xl:flex-none',
          )}
        >
          {searchAction ? (
            <Popover.Root onOpenChange={setIsSearchOpen} open={isSearchOpen}>
              <Popover.Trigger asChild>
                <button
                  aria-label={openSearchPopupLabel}
                  className={navButtonClassName}
                  onPointerEnter={(e) => e.preventDefault()}
                  onPointerLeave={(e) => e.preventDefault()}
                  onPointerMove={(e) => e.preventDefault()}
                >
                  <Search size={20} strokeWidth={1} />
                </button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  align="end"
                  className="max-h-[calc(var(--radix-popover-content-available-height)-16px)] w-(--radix-popper-available-width) py-2 @container data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
                  sideOffset={8}
                >
                  <div className="flex max-h-[inherit] flex-col rounded-2xl bg-(--nav-search-background,hsl(var(--background))) shadow-xl ring-1 ring-(--nav-search-border,hsl(var(--foreground)/5%)) transition-all duration-200 ease-in-out @4xl:inset-x-0">
                    <SearchForm
                      searchAction={searchAction}
                      searchHref={searchHref}
                      searchInputPlaceholder={searchInputPlaceholder}
                      searchParamName={searchParamName}
                      searchSubmitLabel={searchSubmitLabel}
                    />
                  </div>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          ) : (
            <Link aria-label={searchLabel} className={navButtonClassName} href={searchHref}>
              <Search size={20} strokeWidth={1} />
            </Link>
          )}

          {streamableAccountUser ? (
            <Stream fallback={
              <Link aria-label={accountLabel} className={navButtonClassName} href={loginHref}>
                <User size={20} strokeWidth={1} />
              </Link>
            } value={streamableAccountUser}>
              {(user) =>
                user ? (
                  <AccountDropdown
                    accountHref={accountHref}
                    accountLabel={accountLabel}
                  />
                ) : (
                  <Link aria-label={accountLabel} className={navButtonClassName} href={loginHref}>
                    <User size={20} strokeWidth={1} />
                  </Link>
                )
              }
            </Stream>
          ) : (
            <Link aria-label={accountLabel} className={navButtonClassName} href={accountHref}>
              <User size={20} strokeWidth={1} />
            </Link>
          )}
          <Link aria-label={cartLabel} className={navButtonClassName} href={cartHref}>
            <ShoppingBag size={20} strokeWidth={1} />
            <Stream
              fallback={
                <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 animate-pulse items-center justify-center rounded-full bg-contrast-100 text-xs text-background" />
              }
              value={streamableCartCount}
            >
              {(cartCount) =>
                cartCount != null &&
                cartCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-(--nav-cart-count-background,hsl(var(--foreground))) font-[family-name:var(--nav-cart-count-font-family,var(--font-family-body))] text-xs text-(--nav-cart-count-text,hsl(var(--background)))">
                    {cartCount}
                  </span>
                )
              }
            </Stream>
          </Link>

          <Stream fallback={null} value={streamableGiftCertificatesEnabled}>
            {(giftCertificatesEnabled) =>
              giftCertificatesEnabled && (
                <Link
                  aria-label={giftCertificatesLabel}
                  className={navButtonClassName}
                  href={giftCertificatesHref}
                >
                  <GiftIcon size={20} strokeWidth={1} />
                </Link>
              )
            }
          </Stream>

          {/* Locale / Language Dropdown */}
          {locales && locales.length > 1 ? (
            <LocaleSwitcher
              activeLocaleId={activeLocaleId}
              className="hidden @4xl:block"
              locales={locales as [Locale, Locale, ...Locale[]]}
            />
          ) : null}
        </div>
      </div>

      <div
        className="perspective-[2000px] absolute left-0 right-0 top-full z-50 flex w-full justify-center"
        onMouseLeave={() => setNavValue('')}
      >
        <NavigationMenu.Viewport className="relative mt-2 w-full data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95" />
      </div>
    </NavigationMenu.Root>
  );
});

Navigation.displayName = 'Navigation';

function SearchForm<S extends SearchResult>({
  searchAction,
  searchParamName = 'query',
  searchHref = '/search',
  searchInputPlaceholder = 'Search Products',
  searchSubmitLabel = 'Submit',
}: {
  searchAction: SearchAction<S>;
  searchParamName?: string;
  searchHref?: string;
  searchInputPlaceholder?: string;
  searchSubmitLabel?: string;
}) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [isSearching, startSearching] = useTransition();
  const [{ searchResults, lastResult, emptyStateTitle, emptyStateSubtitle }, formAction] =
    useActionState(searchAction, {
      searchResults: null,
      lastResult: null,
    });
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const isPending = isSearching || isDebouncing || isSubmitting;
  const debouncedOnChange = useMemo(() => {
    const debounced = debounce((q: string) => {
      setIsDebouncing(false);

      const formData = new FormData();

      formData.append(searchParamName, q);

      startSearching(() => {
        formAction(formData);
      });
    }, 300);

    return (q: string) => {
      setIsDebouncing(true);

      debounced(q);
    };
  }, [formAction, searchParamName]);

  const [form] = useForm({ lastResult });

  // Reset highlighted index when search results or query change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchResults, query]);

  // Calculate total number of navigable items (products only)
  const totalItems = useMemo(() => {
    if (!searchResults) return 0;
    const productResults = searchResults.find((r) => r.type === 'products');
    return productResults?.type === 'products' ? productResults.products.length : 0;
  }, [searchResults]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (query.trim() === '' || totalItems === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          if (highlightedIndex >= 0) {
            const productResults = searchResults?.find((r) => r.type === 'products');
            if (productResults?.type === 'products') {
              const product = productResults.products[highlightedIndex];
              if (product?.href) {
                e.preventDefault();
                router.push(product.href);
              }
            }
          }
          break;
        case 'Escape':
          setHighlightedIndex(-1);
          break;
      }
    },
    [query, totalItems, highlightedIndex, searchResults, router],
  );

  const handleSubmit = useCallback(() => {
    setIsSubmitting(true);
  }, []);

  return (
    <>
      <form
        action={searchHref}
        className="flex items-center gap-3 px-3 py-3 @4xl:px-5 @4xl:py-4"
        onSubmit={handleSubmit}
      >
        <label htmlFor="search-input" className="sr-only">
          {searchInputPlaceholder}
        </label>
        <SearchIcon
          className="hidden shrink-0 text-(--nav-search-icon,hsl(var(--contrast-500))) @xl:block"
          size={20}
          strokeWidth={1}
        />
        <input
          id="search-input"
          className="grow bg-transparent pl-2 text-lg font-medium outline-0 focus-visible:outline-hidden @xl:pl-0"
          name={searchParamName}
          onChange={(e) => {
            setQuery(e.currentTarget.value);
            debouncedOnChange(e.currentTarget.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={searchInputPlaceholder}
          type="text"
          autoComplete="search"
          value={query}
          role="combobox"
          aria-expanded={query.length > 0 && searchResults !== null}
          aria-controls={searchResults != null && searchResults.length > 0 ? 'search-results' : undefined}
          aria-activedescendant={
            highlightedIndex >= 0 ? `search-result-${highlightedIndex}` : undefined
          }
        />
        <SubmitButton loading={isPending} submitLabel={searchSubmitLabel} />
      </form>

      <SearchResults
        emptySearchSubtitle={emptyStateSubtitle}
        emptySearchTitle={emptyStateTitle}
        errors={form.errors}
        query={query}
        searchParamName={searchParamName}
        searchResults={searchResults}
        stale={isPending}
        highlightedIndex={highlightedIndex}
      />
    </>
  );
}

function SubmitButton({ loading, submitLabel }: { loading: boolean; submitLabel: string }) {
  const { pending } = useFormStatus();

  return (
    <Button
      loading={pending || loading}
      shape="circle"
      size="small"
      type="submit"
      variant="secondary"
    >
      <ArrowRight aria-label={submitLabel} size={20} strokeWidth={1.5} />
    </Button>
  );
}

function SearchResults({
  query,
  searchResults,
  stale,
  emptySearchTitle = `No results were found for '${query}'`,
  emptySearchSubtitle = 'Please try another search.',
  errors,
  highlightedIndex = -1,
}: {
  query: string;
  searchParamName: string;
  emptySearchTitle?: string;
  emptySearchSubtitle?: string;
  searchResults: SearchResult[] | null;
  stale: boolean;
  errors?: string[];
  highlightedIndex?: number;
}) {
  const highlightedRef = useCallback(
    (node: HTMLElement | null) => {
      if (node) {
        node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    },
    [],
  );

  if (query === '') return null;

  if (errors != null && errors.length > 0) {
    if (stale) return null;

    return (
      <div className="flex flex-col border-t border-(--nav-search-divider,hsl(var(--contrast-100))) p-6">
        {errors.map((error) => (
          <FormStatus key={error} type="error">
            {error}
          </FormStatus>
        ))}
      </div>
    );
  }

  if (searchResults == null || searchResults.length === 0) {
    if (stale) return null;

    return (
      <div className="flex flex-col border-t border-(--nav-search-divider,hsl(var(--contrast-100))) p-6">
        <p className="text-2xl font-medium text-(--nav-search-empty-title,hsl(var(--foreground)))">
          {emptySearchTitle}
        </p>
        <p className="text-(--nav-search-empty-subtitle,hsl(var(--contrast-500)))">
          {emptySearchSubtitle}
        </p>
      </div>
    );
  }

  return (
    <div
      id="search-results"
      className={clsx(
        'flex flex-1 flex-col overflow-y-auto border-t border-(--nav-search-divider,hsl(var(--contrast-100))) @2xl:flex-row',
        stale && 'opacity-50',
      )}
    >
      {searchResults.map((result, index) => {
        switch (result.type) {
          case 'links': {
            return (
              <section
                aria-label={result.title}
                className="flex w-full flex-col gap-1 border-b border-(--nav-search-divider,hsl(var(--contrast-100))) p-5 @2xl:max-w-80 @2xl:border-b-0 @2xl:border-r"
                key={`result-${index}`}
              >
                <h3 className="mb-4 font-[family-name:var(--nav-search-result-title-font-family,var(--font-family-mono))] text-sm uppercase text-(--nav-search-result-title,hsl(var(--foreground)))">
                  {result.title}
                </h3>
                <ul role="listbox">
                  {result.links.map((link, i) => (
                    <li key={i}>
                      <Link
                        className="block rounded-lg bg-(--nav-search-result-link-background,transparent) px-3 py-4 font-[family-name:var(--nav-search-result-link-font-family,var(--font-family-body))] font-semibold text-(--nav-search-result-link-text,hsl(var(--contrast-500))) ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-search-result-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-search-result-link-text-hover,hsl(var(--foreground))) focus-visible:outline-0 focus-visible:ring-2"
                        href={link.href}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          }

          case 'products': {
            return (
              <section
                aria-label={result.title}
                className="flex w-full flex-col gap-5 p-5"
                key={`result-${index}`}
              >
                <h3 className="font-[family-name:var(--nav-search-result-title-font-family,var(--font-family-mono))] text-sm uppercase text-(--nav-search-result-title,hsl(var(--foreground)))">
                  {result.title}
                </h3>
                <ul
                  className="grid w-full grid-cols-2 gap-5 @xl:grid-cols-4 @2xl:grid-cols-2 @4xl:grid-cols-4"
                  role="listbox"
                >
                  {result.products.map((product, productIndex) => {
                    const isHighlighted = productIndex === highlightedIndex;
                    return (
                      <li
                        key={product.id}
                        id={`search-result-${productIndex}`}
                        ref={isHighlighted ? highlightedRef : undefined}
                        role="option"
                        aria-selected={isHighlighted}
                        className={clsx(
                          'rounded-lg transition-all',
                          isHighlighted &&
                            'ring-2 ring-(--nav-focus,hsl(var(--primary))) ring-offset-2',
                        )}
                      >
                        <ProductCard
                          imageSizes="(min-width: 42rem) 25vw, 50vw"
                          product={{
                            id: product.id,
                            title: product.title,
                            href: product.href,
                            price: product.price,
                            image: product.image,
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          }

          default:
            return null;
        }
      })}
    </div>
  );
}

const useSwitchLocale = () => {
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();

  return useCallback(
    (locale: string) =>
      router.push(
        // @ts-expect-error -- TypeScript will validate that only known `params`
        // are used in combination with a given `pathname`. Since the two will
        // always match for the current route, we can skip runtime checks.
        { pathname, params, query: Object.fromEntries(searchParams.entries()) },
        { locale },
      ),
    [pathname, params, router, searchParams],
  );
};

function LocaleSwitcher({
  locales,
  activeLocaleId,
  className,
}: {
  activeLocaleId?: string;
  locales: [Locale, ...Locale[]];
  className?: string;
}) {
  const activeLocale = locales.find((locale) => locale.id === activeLocaleId);
  const [isPending, startTransition] = useTransition();
  const switchLocale = useSwitchLocale();

  return (
    <div className={className}>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          className={clsx(
            'flex items-center gap-1 text-xs uppercase transition-opacity disabled:opacity-30',
            navButtonClassName,
          )}
          disabled={isPending}
        >
          {activeLocale?.id ?? locales[0].id}
          <ChevronDown size={16} strokeWidth={1.5} />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            className="z-50 max-h-80 overflow-y-scroll rounded-xl bg-(--nav-locale-background,hsl(var(--background))) p-2 shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 @4xl:w-32 @4xl:rounded-2xl @4xl:p-2"
            sideOffset={16}
          >
            {locales.map(({ id, label }) => (
              <DropdownMenu.Item
                className={clsx(
                  'cursor-default rounded-lg bg-(--nav-locale-link-background,transparent) px-2.5 py-2 font-[family-name:var(--nav-locale-link-font-family,var(--font-family-body))] text-sm font-medium text-(--nav-locale-link-text,hsl(var(--contrast-400))) outline-hidden ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-(--nav-locale-link-background-hover,hsl(var(--contrast-100))) hover:text-(--nav-locale-link-text-hover,hsl(var(--foreground)))',
                  {
                    'text-(--nav-locale-link-text-selected,hsl(var(--foreground)))':
                      id === activeLocaleId,
                  },
                )}
                key={id}
                onSelect={() => startTransition(() => switchLocale(id))}
              >
                {label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}

const accountDropdownItemClassName =
  'flex cursor-default items-center gap-2 rounded-lg bg-transparent px-2.5 py-2 font-[family-name:var(--nav-locale-link-font-family,var(--font-family-body))] text-sm font-medium text-[hsl(var(--secondary-foreground))] outline-hidden ring-(--nav-focus,hsl(var(--primary))) transition-colors hover:bg-[hsl(var(--contrast-100))] hover:text-foreground';

function AccountDropdown({
  accountHref,
  accountLabel,
}: {
  accountHref: string;
  accountLabel: string;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={clsx(
          'flex items-center gap-0.5',
          navButtonClassName,
        )}
      >
        <User size={20} strokeWidth={1} />
        <ChevronDown className="hidden @4xl:block" size={14} strokeWidth={1.5} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          className="z-50 min-w-[180px] rounded-xl bg-white p-2 shadow-xl ring-1 ring-black/5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 @4xl:rounded-2xl @4xl:p-2"
          sideOffset={16}
        >
          <DropdownMenu.Item asChild>
            <Link className={accountDropdownItemClassName} href="/account/loyalty/">
              Unur Club
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link className={accountDropdownItemClassName} href="/account/orders/">
              Orders
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link className={accountDropdownItemClassName} href="/account/addresses/">
              Addresses
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link className={accountDropdownItemClassName} href={accountHref}>
              {accountLabel}
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Link className={accountDropdownItemClassName} href="/account/wishlists/">
              Wish Lists
            </Link>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-[hsl(var(--contrast-100))]" />
          <DropdownMenu.Item asChild>
            <Link
              className={accountDropdownItemClassName}
              href="/logout"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                window.location.href = e.currentTarget.href;
              }}
            >
              <LogOut size={14} strokeWidth={1.5} />
              Logout
            </Link>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
